import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface SimpleRichTextEditorProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function SimpleRichTextEditor({ value, onChange, placeholder = "Start writing...", className }: SimpleRichTextEditorProps) {
  const { toast } = useToast();
  const [QuillComponent, setQuillComponent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const quillRef = useRef<any>(null);
  const [pasteData, setPasteData] = useState<{ text: string; html: string; index: number } | null>(null);

  useEffect(() => {
    // Dynamically import react-quill to avoid SSR issues
    const loadQuill = async () => {
      try {
        const ReactQuill = (await import("react-quill")).default;
        await import("react-quill/dist/quill.snow.css");

        const Quill = ReactQuill.Quill;
        const BlockEmbed = Quill.import("blots/block/embed");

        class TableEmbed extends BlockEmbed {
          static create(value: string) {
            const node = super.create();
            node.setAttribute("contenteditable", "false");
            node.innerHTML = value;
            return node;
          }

          static value(node: HTMLElement) {
            return node.innerHTML;
          }
        }

        TableEmbed.blotName = "table-embed";
        TableEmbed.tagName = "div";
        TableEmbed.className = "quill-table-embed-wrapper";

        Quill.register(TableEmbed, true);

        setQuillComponent(() => ReactQuill);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load Quill:", error);
        setIsLoading(false);
      }
    };

    loadQuill();
  }, []);

  useEffect(() => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    if (!editor) return;

    const handlePaste = (e: ClipboardEvent) => {
      const clipboardData = e.clipboardData;
      if (!clipboardData) return;

      const html = clipboardData.getData("text/html");
      const text = clipboardData.getData("text/plain");

      if (!html && !text) return;

      const index = editor.getSelection()?.index ?? editor.getLength();
      const isTable = detectTableFormat(html, text);

      if (isTable) {
        e.preventDefault();
        const tableHtml = formatAsTable(html, text);
        editor.insertEmbed(index, "table-embed", tableHtml, "user");
        editor.insertText(index + 1, "\n", "user");
        editor.setSelection(index + 2, "user");
        toast({
          title: "Table Pasted",
          description: "Table has been successfully formatted and pasted.",
        });
      } else {
        // Intercept regular text paste to prompt user if they want to display as table or text
        e.preventDefault();
        setPasteData({ text, html, index });
      }
    };

    const root = editor.root;
    root.addEventListener("paste", handlePaste);
    return () => {
      root.removeEventListener("paste", handlePaste);
    };
  }, [QuillComponent]);

  const detectTableFormat = (html: string, text: string): boolean => {
    if (!html && !text) return false;

    // Check for HTML table format
    if (html && /<table[^>]*>[\s\S]*?<\/table>/i.test(html)) {
      return true;
    }

    if (!text) return false;
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return false;

    // Check for TSV format (Excel/Google Sheets)
    const tabCounts = lines.map(l => (l.match(/\t/g) || []).length);
    const hasTabs = tabCounts.every(c => c > 0 && c === tabCounts[0]);
    if (hasTabs) return true;

    // Check for Markdown table
    const hasMarkdown = lines.some(l => l.startsWith("|") && l.endsWith("|")) &&
                        lines.some(l => l.includes("|") && /^[ :|-]+$/.test(l.replace(/\|/g, "").trim()));
    if (hasMarkdown) return true;

    // Check for CSV / Semicolon-separated values
    const commaCounts = lines.map(l => (l.match(/,/g) || []).length);
    const hasCommas = commaCounts.every(c => c > 0 && c === commaCounts[0]);
    if (hasCommas) return true;

    const semiCounts = lines.map(l => (l.match(/;/g) || []).length);
    const hasSemis = semiCounts.every(c => c > 0 && c === semiCounts[0]);
    if (hasSemis) return true;

    return false;
  };

  const escapeHtml = (str: string): string => {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const formatAsTable = (html: string, text: string): string => {
    if (html && /<table[^>]*>[\s\S]*?<\/table>/i.test(html)) {
      const match = html.match(/<table[^>]*>[\s\S]*?<\/table>/i);
      if (match) {
        let tableHtml = match[0];
        tableHtml = tableHtml
          .replace(/<table/gi, '<table class="min-w-full border-collapse border border-gray-200 my-4"')
          .replace(/<th/gi, '<th class="border border-gray-200 bg-gray-50 px-4 py-2 text-left font-semibold text-gray-700"')
          .replace(/<td/gi, '<td class="border border-gray-200 px-4 py-2 text-gray-600"');
        return tableHtml;
      }
    }

    // Parse Markdown table
    if (text.includes("|")) {
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      const tableLines = lines.filter(l => l.startsWith("|") && l.endsWith("|"));
      if (tableLines.length > 0) {
        let htmlOutput = '<table class="min-w-full border-collapse border border-gray-200 my-4">';
        tableLines.forEach((line, rowIndex) => {
          if (line.includes("---") || /^[|:\s-]+$/.test(line)) return;

          const cells = line.split("|").map(c => c.trim()).slice(1, -1);
          htmlOutput += "<tr>";
          cells.forEach(cell => {
            const tag = rowIndex === 0 ? "th" : "td";
            const cellClass = rowIndex === 0
              ? "border border-gray-200 bg-gray-50 px-4 py-2 text-left font-semibold text-gray-700"
              : "border border-gray-200 px-4 py-2 text-gray-600";
            htmlOutput += `<${tag} class="${cellClass}">${cell}</${tag}>`;
          });
          htmlOutput += "</tr>";
        });
        htmlOutput += "</table>";
        return htmlOutput;
      }
    }

    // Parse TSV/CSV/Plain lines
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length > 0) {
      let delimiter = "";
      if (lines.some(l => l.includes("\t"))) delimiter = "\t";
      else if (lines.every(l => l.includes(";")) || lines.filter(l => l.includes(";")).length > lines.length / 2) delimiter = ";";
      else if (lines.every(l => l.includes(",")) || lines.filter(l => l.includes(",")).length > lines.length / 2) delimiter = ",";

      let htmlOutput = '<table class="min-w-full border-collapse border border-gray-200 my-4">';
      lines.forEach((line, rowIndex) => {
        const cells = delimiter ? line.split(delimiter) : line.split(/\s{2,}/);
        htmlOutput += "<tr>";

        const parsedCells = cells.length > 0 ? cells : [line];
        parsedCells.forEach(cell => {
          const tag = rowIndex === 0 ? "th" : "td";
          const cellClass = rowIndex === 0
            ? "border border-gray-200 bg-gray-50 px-4 py-2 text-left font-semibold text-gray-700"
            : "border border-gray-200 px-4 py-2 text-gray-600";
          htmlOutput += `<${tag} class="${cellClass}">${escapeHtml(cell.trim())}</${tag}>`;
        });
        htmlOutput += "</tr>";
      });
      htmlOutput += "</table>";
      return htmlOutput;
    }

    return "";
  };

  const pasteAsText = () => {
    if (!pasteData || !quillRef.current) return;
    const editor = quillRef.current.getEditor();
    
    if (pasteData.html) {
      editor.clipboard.dangerouslyPasteHTML(pasteData.index, pasteData.html, "user");
    } else {
      editor.insertText(pasteData.index, pasteData.text, "user");
    }
    setPasteData(null);
  };

  const pasteAsTable = () => {
    if (!pasteData || !quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const tableHtml = formatAsTable(pasteData.html, pasteData.text);

    editor.insertEmbed(pasteData.index, "table-embed", tableHtml, "user");
    editor.insertText(pasteData.index + 1, "\n", "user");
    editor.setSelection(pasteData.index + 2, "user");
    setPasteData(null);
    
    toast({
      title: "Converted to Table",
      description: "Text content successfully converted to table format.",
    });
  };

  const handleImageUpload = async () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        const formData = new FormData();
        formData.append("image", file);

        const response = await fetch("/api/upload/blog-image", {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          throw new Error("Failed to upload image");
        }

        const data = await response.json();
        const imageUrl = data.url;

        // Simple way to insert image - append to content
        const currentContent = value || "";
        const imageHtml = `<img src="${imageUrl}" alt="Uploaded image" style="max-width: 100%; height: auto;" />`;
        onChange(currentContent + imageHtml);

        toast({
          title: "Success",
          description: "Image uploaded successfully",
        });
      } catch (error) {
        console.error("Image upload error:", error);
        toast({
          title: "Error",
          description: "Failed to upload image",
          variant: "destructive",
        });
      }
    };
  };

  if (isLoading) {
    return (
      <div className={`border rounded-lg p-4 ${className}`}>
        <div className="text-center text-gray-500">
          Loading rich text editor...
        </div>
      </div>
    );
  }

  if (!QuillComponent) {
    // Fallback to a rich textarea with basic formatting
    return (
      <div className={`border rounded-lg ${className}`}>
        <div className="bg-gray-50 border-b px-3 py-2">
          <span className="text-sm text-gray-500">
            Rich text editor
          </span>
        </div>
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full h-[350px] p-3 border-0 resize-none focus:outline-none"
          style={{ fontFamily: "inherit" }}
        />
      </div>
    );
  }

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'link', 'align', 'code-block', 'table-embed'
  ];

  return (
    <>
      <div className={`rich-text-editor ${className}`}>
        <div className="border border-gray-300 rounded-md">
          <QuillComponent
            ref={quillRef}
            theme="snow"
            value={value || ""}
            onChange={onChange}
            modules={modules}
            formats={formats}
            placeholder={placeholder}
            style={{ height: "400px" }}
          />
        </div>
        <style>{`
          .ql-container {
            height: 350px !important;
            font-size: 14px;
          }
          .ql-editor {
            height: 100% !important;
            min-height: 350px !important;
            max-height: none !important;
            overflow-y: auto;
            font-size: 14px;
            line-height: 1.6;
            padding: 12px 15px;
          }
          .ql-toolbar {
            border-bottom: 1px solid #ccc;
            font-size: 14px;
          }
        `}</style>
      </div>

      {pasteData && (
        <Dialog open={!!pasteData} onOpenChange={(open) => { if (!open) setPasteData(null); }}>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle>Paste Options</DialogTitle>
              <DialogDescription>
                We noticed you are pasting text content. How would you like to display this inside the case study?
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-md border border-gray-100 max-h-[120px] overflow-y-auto">
                <span className="font-semibold block mb-1">Preview:</span>
                <span className="font-mono whitespace-pre-wrap">
                  {pasteData.text.slice(0, 300)}{pasteData.text.length > 300 ? "..." : ""}
                </span>
              </div>
            </div>
            <DialogFooter className="flex flex-col sm:flex-row gap-2 justify-end">
              <Button variant="outline" onClick={pasteAsText} className="w-full sm:w-auto">
                Paste as Text
              </Button>
              <Button onClick={pasteAsTable} className="w-full sm:w-auto">
                Paste as Table Format
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}